/**
 * Release QA Hooks
 *
 * React Query hooks for Release QA runs
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { throwReleaseApiError } from './releaseApiError';
import type {
  ReleaseRunsResponse,
  ReleaseRunDetailResponse,
  StartReleaseRunResponse,
  ReleaseTestersResponse,
  ReleaseRegressionsResponse,
  ReleaseQATesterStatus,
  ReleaseRegressionStatus,
  ReleaseQAConfig,
  ReleaseQAConfigResponse,
  SaveReleaseConfigInput,
  PropertyGitHubReposResponse,
  ReleaseAgentResponse,
} from './releaseTypes';
import { isActiveStatus, isTerminalAgentStatus } from './releaseTypes';

type PaginationOptions = {
  page?: number;
  pageSize?: number;
};

type ListReleasesOptions = PaginationOptions & {
  propertyId?: string;
};

type StartReleaseRunOptions = {
  propertyId: string;
  credentialIds?: string[];
  /** Full commit sha to test; omitted tests the release branch head. */
  sha?: string;
};

/**
 * Fetch all release QA runs for the current organization.
 */
export function useReleaseRuns(options?: ListReleasesOptions) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;
  const propertyId = options?.propertyId;

  return useQuery({
    queryKey: ['release-qa-runs', page, pageSize, propertyId],
    queryFn: async (): Promise<ReleaseRunsResponse> => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (propertyId) {
        params.set('propertyId', propertyId);
      }

      const response = await fetch(`${API_URL}/release-qa/runs?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch release runs');
      }

      return response.json();
    },
  });
}

/**
 * Fetch a single release QA run by ID.
 */
export function useReleaseRun(id: string | undefined) {
  return useQuery({
    queryKey: ['release-qa-run', id],
    queryFn: async (): Promise<ReleaseRunDetailResponse> => {
      if (!id) {
        throw new Error('Missing release run ID');
      }

      const response = await fetch(`${API_URL}/release-qa/runs/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch release run');
      }

      return response.json();
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status;
      return isActiveStatus(status) ? 3000 : false;
    },
  });
}

/**
 * Fetch testers for a release QA run with pagination and optional status filter.
 */
export function useReleaseRunTesters(
  releaseRunId: string | undefined,
  options?: PaginationOptions & { status?: ReleaseQATesterStatus }
) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;
  const status = options?.status;

  return useQuery({
    queryKey: ['release-qa-testers', releaseRunId, page, pageSize, status],
    queryFn: async (): Promise<ReleaseTestersResponse> => {
      if (!releaseRunId) {
        throw new Error('Missing release run ID');
      }

      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (status) {
        params.set('status', status);
      }

      const response = await fetch(`${API_URL}/release-qa/runs/${releaseRunId}/testers?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch release testers');
      }

      return response.json();
    },
    enabled: !!releaseRunId,
    refetchInterval: (query) => {
      // Refetch while there are active testers
      const testers = query.state.data?.testers ?? [];
      const hasActiveTesters = testers.some(
        (t) => t.status === 'running' || t.status === 'queued' || t.status === 'pending'
      );
      return hasActiveTesters ? 3000 : false;
    },
  });
}

/**
 * Fetch regression test results for a release QA run with pagination.
 */
export function useReleaseRunRegressions(
  releaseRunId: string | undefined,
  options?: PaginationOptions & { status?: ReleaseRegressionStatus }
) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;
  const status = options?.status;

  return useQuery({
    queryKey: ['release-qa-regressions', releaseRunId, page, pageSize, status],
    queryFn: async (): Promise<ReleaseRegressionsResponse> => {
      if (!releaseRunId) {
        throw new Error('Missing release run ID');
      }

      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (status) {
        params.set('status', status);
      }

      const response = await fetch(
        `${API_URL}/release-qa/runs/${releaseRunId}/regressions?${params}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch release regressions');
      }

      return response.json();
    },
    enabled: !!releaseRunId,
  });
}

/**
 * Start a new release QA run.
 */
export function useStartReleaseRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: StartReleaseRunOptions): Promise<StartReleaseRunResponse> => {
      const response = await fetch(`${API_URL}/release-qa/runs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: options.propertyId,
          credentialIds: options.credentialIds,
          sha: options.sha,
        }),
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to start release run');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['release-qa-runs'] });
    },
  });
}

/**
 * Cancel a running release QA run.
 */
export function useCancelReleaseRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ ok: boolean }> => {
      const response = await fetch(`${API_URL}/release-qa/runs/${id}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to cancel release run');
      }

      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['release-qa-run', id] });
      queryClient.invalidateQueries({ queryKey: ['release-qa-runs'] });
      queryClient.invalidateQueries({ queryKey: ['release-qa-testers', id] });
      queryClient.invalidateQueries({ queryKey: ['release-qa-regressions', id] });
    },
  });
}

/**
 * Delete a release QA run.
 */
export function useDeleteReleaseRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<{ ok: boolean }> => {
      const response = await fetch(`${API_URL}/release-qa/runs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete release run');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['release-qa-runs'] });
      queryClient.invalidateQueries({ queryKey: ['release-qa-timeline'] });
    },
  });
}

/**
 * Update the title of a release QA run.
 */
function _useUpdateReleaseRunTitle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }): Promise<{ ok: boolean }> => {
      const response = await fetch(`${API_URL}/release-qa/runs/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update release run title');
      }

      return response.json();
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['release-qa-run', id] });
      queryClient.invalidateQueries({ queryKey: ['release-qa-runs'] });
    },
  });
}

// ============================================================================
// Release QA Configuration Hooks
// ============================================================================

/**
 * Fetch Release QA config for a property.
 */
export function useReleaseConfig(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['release-qa-config', propertyId],
    queryFn: async (): Promise<ReleaseQAConfigResponse> => {
      const response = await fetch(`${API_URL}/release-qa/configs/${propertyId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { ok: true, config: null };
        }
        await throwReleaseApiError(response, 'Failed to fetch release config');
      }

      return response.json();
    },
    enabled: !!propertyId,
  });
}

/**
 * Fetch linked GitHub repos for a property.
 */
export function usePropertyGitHubRepos(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['property-github-repos', propertyId],
    queryFn: async (): Promise<PropertyGitHubReposResponse> => {
      const response = await fetch(`${API_URL}/org/properties/${propertyId}/github-repos`, {
        credentials: 'include',
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch linked repos');
      }

      return response.json();
    },
    enabled: !!propertyId,
  });
}

/**
 * Create or update Release QA config.
 */
export function useSaveReleaseConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: SaveReleaseConfigInput
    ): Promise<{ ok: boolean; config: ReleaseQAConfig }> => {
      const { propertyId, isNew, ...data } = input;
      const url = isNew
        ? `${API_URL}/release-qa/configs`
        : `${API_URL}/release-qa/configs/${propertyId}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isNew ? { propertyId, ...data } : data),
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to save release config');
      }

      return response.json();
    },
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ['release-qa-config', propertyId] });
    },
  });
}

/**
 * Delete Release QA config.
 */
export function useDeleteReleaseConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (propertyId: string): Promise<{ ok: boolean }> => {
      const response = await fetch(`${API_URL}/release-qa/configs/${propertyId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete release config');
      }

      return response.json();
    },
    onSuccess: (_, propertyId) => {
      queryClient.invalidateQueries({ queryKey: ['release-qa-config', propertyId] });
    },
  });
}

/**
 * Fetch the release agent's status (plus open questions and transcript)
 * for a release run — the web reads only the status, to auto-open the
 * assistant on a live agent (`useReleaseAgentAutoOpen`); replies go
 * through the assistant's `releases.reply_to_agent`. Polls every 15s while
 * the agent is not terminal.
 */
export function useReleaseAgent(runId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['release-qa-agent', runId],
    queryFn: async (): Promise<ReleaseAgentResponse> => {
      if (!runId) {
        throw new Error('Missing release run ID');
      }

      const response = await fetch(`${API_URL}/release-qa/runs/${runId}/agent`, {
        credentials: 'include',
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch release agent');
      }

      return response.json();
    },
    enabled: enabled && !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.agent?.status;
      return isTerminalAgentStatus(status) ? false : 15_000;
    },
  });
}

/**
 * Validate a cron pattern and get next run times.
 */
export function useValidateCron() {
  return useMutation({
    mutationFn: async ({ pattern, timezone }: { pattern: string; timezone: string }) => {
      const response = await fetch(`${API_URL}/schedules/validate-cron`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pattern, timezone }),
      });

      if (!response.ok) {
        throw new Error('Failed to validate cron pattern');
      }

      return response.json() as Promise<{
        ok: boolean;
        valid: boolean;
        error?: string;
        nextRuns?: string[];
      }>;
    },
  });
}
